'use client'

// SUMMARY: Consent banner for § 25 TTDSG / Art. 6+7 GDPR. Three EQUALLY PROMINENT first-layer choices -
// accept all, necessary only, reject - because German supervisory authorities treat a highlighted "accept"
// next to a muted "reject" as invalid consent. Nothing optional is stored before a decision, no box is
// pre-ticked, and the decision is revocable from the footer at any time.

import { useCallback, useEffect, useState } from 'react'
import Link from 'next/link'
import { useTranslations } from 'next-intl'
import { Cookie } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { CONSENT_CHANGED, readConsent, saveConsent } from '@/lib/consent'

/** Opening the settings dialog from anywhere else (the footer link) is a plain window event. */
export const OPEN_CONSENT_SETTINGS = 'evuno:open-consent-settings'

export function openConsentSettings(): void {
	window.dispatchEvent(new Event(OPEN_CONSENT_SETTINGS))
}

export function CookieBanner() {
	const t = useTranslations('consent')
	// `undecided` starts null so the server-rendered markup and the first client render agree; the cookie
	// is only readable in the browser.
	const [undecided, setUndecided] = useState<boolean | null>(null)
	const [settingsOpen, setSettingsOpen] = useState(false)
	const [functionalDraft, setFunctionalDraft] = useState(false)

	useEffect(() => {
		const sync = () => {
			const consent = readConsent()
			setUndecided(consent === null)
			setFunctionalDraft(consent?.functional === true)
		}
		sync()
		const onOpenSettings = () => {
			sync()
			setSettingsOpen(true)
		}
		window.addEventListener(CONSENT_CHANGED, sync)
		window.addEventListener(OPEN_CONSENT_SETTINGS, onOpenSettings)
		return () => {
			window.removeEventListener(CONSENT_CHANGED, sync)
			window.removeEventListener(OPEN_CONSENT_SETTINGS, onOpenSettings)
		}
	}, [])

	const decide = useCallback((functional: boolean) => {
		saveConsent(functional)
		setUndecided(false)
		setSettingsOpen(false)
	}, [])

	const legalLinks = (
		<>
			<Link href="/privacy" className="underline underline-offset-2 hover:text-foreground">
				{t('privacy')}
			</Link>
			{' · '}
			<Link href="/imprint" className="underline underline-offset-2 hover:text-foreground">
				{t('imprint')}
			</Link>
		</>
	)

	return (
		<>
			{undecided === true && (
				<div
					role="dialog"
					aria-modal="false"
					aria-labelledby="consent-title"
					className="fixed inset-x-0 bottom-0 z-[60] p-3 sm:inset-x-auto sm:right-4 sm:bottom-4 sm:p-0"
				>
					{/* Bottom-right card rather than a full-width bar: it keeps the page readable while deciding.
					    Full width only below sm, where a corner card would be a corner in name only. */}
					<div className="w-full rounded-xl border bg-card/95 p-4 shadow-lg backdrop-blur-md sm:w-[34rem] sm:p-5 lg:w-[40rem]">
						<div className="flex gap-3">
							<div className="hidden h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-100 sm:flex dark:bg-blue-950">
								<Cookie className="h-4.5 w-4.5 text-blue-600 dark:text-blue-400" />
							</div>
							<div className="min-w-0">
								<h2 id="consent-title" className="text-sm font-semibold">
									{t('title')}
								</h2>
								<p className="mt-1 text-sm text-muted-foreground">{t('description')}</p>
								<p className="mt-2 text-xs text-muted-foreground">{legalLinks}</p>
							</div>
						</div>

						{/* All three choices are the same size in the same row: rejecting must be exactly as easy
						    as accepting, which is the point German authorities enforce most often. */}
						<div className="mt-4 flex flex-col gap-2 sm:flex-row">
							<Button onClick={() => decide(true)} className="sm:flex-1">
								{t('acceptAll')}
							</Button>
							<Button variant="outline" onClick={() => decide(false)} className="sm:flex-1">
								{t('necessaryOnly')}
							</Button>
							<Button variant="outline" onClick={() => decide(false)} className="sm:flex-1">
								{t('rejectAll')}
							</Button>
						</div>
						<button
							type="button"
							onClick={() => setSettingsOpen(true)}
							className="mt-3 cursor-pointer text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
						>
							{t('settings')}
						</button>
					</div>
				</div>
			)}

			<Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
				<DialogContent className="max-w-lg">
					<DialogHeader>
						<DialogTitle>{t('settingsTitle')}</DialogTitle>
						<DialogDescription>{t('settingsDescription')}</DialogDescription>
					</DialogHeader>

					<div className="space-y-3">
						<CategoryRow
							title={t('categories.necessary.title')}
							description={t('categories.necessary.description')}
							examples={t('categories.necessary.examples')}
							alwaysOn={t('alwaysOn')}
						/>
						<CategoryRow
							title={t('categories.functional.title')}
							description={t('categories.functional.description')}
							examples={t('categories.functional.examples')}
							checked={functionalDraft}
							onChange={setFunctionalDraft}
						/>
						<p className="text-xs text-muted-foreground">{t('noTracking')}</p>
					</div>

					<div className="flex flex-col gap-2 sm:flex-row">
						<Button onClick={() => decide(functionalDraft)} className="sm:flex-1">
							{t('save')}
						</Button>
						<Button variant="outline" onClick={() => decide(true)} className="sm:flex-1">
							{t('acceptAll')}
						</Button>
						<Button variant="outline" onClick={() => decide(false)} className="sm:flex-1">
							{t('rejectAll')}
						</Button>
					</div>
					<p className="text-xs text-muted-foreground">{legalLinks}</p>
				</DialogContent>
			</Dialog>
		</>
	)
}

function CategoryRow({
	title,
	description,
	examples,
	checked,
	onChange,
	alwaysOn,
}: {
	title: string
	description: string
	examples: string
	checked?: boolean
	onChange?: (next: boolean) => void
	alwaysOn?: string
}) {
	return (
		<div className="rounded-lg border p-3">
			<div className="flex items-start justify-between gap-3">
				<div className="min-w-0">
					<p className="text-sm font-medium">{title}</p>
					<p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
				</div>
				{onChange ? (
					// Unchecked by default - a pre-ticked box is not consent (Art. 4 No. 11 GDPR, Planet49).
					<Switch checked={checked} onCheckedChange={onChange} aria-label={title} />
				) : (
					<span className="shrink-0 rounded-md bg-muted px-2 py-1 text-[11px] text-muted-foreground">{alwaysOn}</span>
				)}
			</div>
			<p className="mt-2 text-[11px] text-muted-foreground">{examples}</p>
		</div>
	)
}
